import React, { useCallback, useRef, useState } from 'react';
import { getGmailStatus, getGmailAuthUrl } from '../services/api';
import { captureEvent } from '../services/analytics';
import { handleError } from '../utils/errorUtils';
import {
  setGmailReturnTo,
  clearGmailReturnTo,
  setAutoApplyResume,
  clearAutoApplyResume,
  AutoApplyResumeIntent,
} from '../utils/gmailReturn';
import { useGmailConsentPrimer } from './useGmailConsentPrimer';
import AutoApplyGmailGateModal, {
  AutoApplyGateVariant,
} from '../components/settings/AutoApplyGmailGateModal';
import { AutoApplyOfferSurface } from '../types';

interface AutoApplyGate {
  /**
   * The entry point every "Set up Auto-Apply" control calls instead of opening
   * the activation modal directly. Resolves to opening either the activation
   * modal (Gmail can send) or the gate (it cannot).
   */
  requestSetup: () => void;
  /**
   * Open the gate directly for an already-enabled user whose Gmail broke. The
   * caller passes the variant because it already knows which half is missing —
   * assuming `fix` here told a user with no Gmail at all that they needed to
   * grant a permission, and sent them through the wrong primer.
   */
  requestRepair: (variant: AutoApplyGateVariant) => void;
  /**
   * The activation modal's save came back 409. Same gate, but the intent stays
   * `setup` — the user was mid-activation, so on return they should land back
   * in the activation modal, not on a "you can send again" toast.
   */
  showGateAfterSaveError: (variant: AutoApplyGateVariant) => void;
  /** True while the Gmail probe is in flight; hosts disable their CTA on it. */
  probing: boolean;
  /** Render once in the host's JSX: the gate modal plus the consent primer. */
  gate: React.ReactElement;
}

/**
 * Owns one question — "may this user open the Auto-Apply activation modal?" —
 * for every surface that asks it (the Settings card, the offer banner on
 * Opportunities and Applications).
 *
 * Gmail status is probed on the click rather than held in context: it is
 * freshest at the only moment it matters, costs one request on an explicit
 * gesture, and needs no new global state.
 */
export function useAutoApplyGate(
  onAllowed: () => void,
  surface: AutoApplyOfferSurface | 'settings'
): AutoApplyGate {
  const [gateOpen, setGateOpen] = useState(false);
  const [variant, setVariant] = useState<AutoApplyGateVariant>('connect');
  const [probing, setProbing] = useState(false);
  // The resume intent belongs to whichever entry point opened the gate, and is
  // only committed to storage once the user actually leaves for Google.
  const intentRef = useRef<AutoApplyResumeIntent>('setup');
  // Monotonic id: a stale probe response must never act. Without it a
  // double-click can land two responses out of order and open the gate over the
  // activation modal, or the reverse.
  const probeIdRef = useRef(0);

  const openGate = useCallback(
    (nextVariant: AutoApplyGateVariant, intent: AutoApplyResumeIntent) => {
      intentRef.current = intent;
      setVariant(nextVariant);
      setGateOpen(true);
      captureEvent('auto_apply_gmail_gate_shown', { variant: nextVariant, surface, intent });
    },
    [surface]
  );

  const requestSetup = useCallback(async () => {
    if (probing) return;
    const probeId = ++probeIdRef.current;
    setProbing(true);

    // ONLY the network call is guarded. Widening this try to cover the branch
    // below would make a throw from onAllowed/openGate — a render error, not a
    // network one — land in the fail-open handler and invoke onAllowed a second
    // time. `status` stays null to mean "we could not ask".
    let status: Awaited<ReturnType<typeof getGmailStatus>> | null = null;
    try {
      status = await getGmailStatus();
    } catch {
      status = null;
    } finally {
      if (probeId === probeIdRef.current) setProbing(false);
    }

    if (probeId !== probeIdRef.current) return; // superseded by a later click

    // FAIL OPEN on an unanswered probe. The server's 409 is the real
    // enforcement and hands back to this gate with the user's work still on
    // screen, so a flaky status call costs one extra click — where failing
    // closed would block a working flow every time the network hiccups.
    //
    // Falsy, not `=== null`: a response we cannot read (an empty body, a shape
    // change) is every bit as much "we could not ask" as a rejected promise, and
    // it must not become a TypeError on the next line.
    if (!status || (status.canSend !== false && status.connected)) {
      onAllowed();
      return;
    }
    openGate(status.connected ? 'fix' : 'connect', 'setup');
  }, [onAllowed, openGate, probing]);

  const requestRepair = useCallback(
    // The caller already knows the state from the Settings Gmail card; no probe.
    (nextVariant: AutoApplyGateVariant) => openGate(nextVariant, 'repair'),
    [openGate]
  );

  const showGateAfterSaveError = useCallback(
    (nextVariant: AutoApplyGateVariant) => openGate(nextVariant, 'setup'),
    [openGate]
  );

  /**
   * Runs inside the primer's Continue click, immediately before leaving for
   * Google — NOT on the gate's Connect button. The primer is cancellable and
   * this URL fetch can fail; flags written any earlier would outlive an
   * abandoned connect and reopen Auto-Apply on an unrelated arrival within the
   * TTL.
   */
  const startConnect = useCallback(async () => {
    setGmailReturnTo('/settings');
    setAutoApplyResume(intentRef.current);
    try {
      const { authUrl } = await getGmailAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      clearGmailReturnTo();
      clearAutoApplyResume();
      handleError(error);
    }
  }, []);

  const { openPrimer, primer } = useGmailConsentPrimer(startConnect);

  const handleConnect = () => {
    captureEvent('auto_apply_gmail_gate_connect_clicked', {
      variant,
      surface,
      intent: intentRef.current,
    });
    setGateOpen(false);
    openPrimer(variant);
  };

  const handleClose = () => {
    captureEvent('auto_apply_gmail_gate_dismissed', { variant, surface });
    setGateOpen(false);
  };

  const gate = (
    <>
      <AutoApplyGmailGateModal
        open={gateOpen}
        variant={variant}
        onConnect={handleConnect}
        onClose={handleClose}
      />
      {primer}
    </>
  );

  const requestSetupStable = useCallback(() => void requestSetup(), [requestSetup]);

  return {
    requestSetup: requestSetupStable,
    requestRepair,
    showGateAfterSaveError,
    probing,
    gate,
  };
}
